import React, { useEffect, useRef } from 'react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { PersonProps } from 'Components/common/components/PeopleCardLarge';
import { usePrevious } from 'Utils/hooks/usePrevious';
import classNames from 'classnames';
import { dragInteractionStyle } from 'Utils/performance/renderOptimizations';
import { useSelector } from 'react-redux';
import { selectSelectedPeople } from 'Selector/GlobalSearchResultsSelector';

type Props = {
  children: JSX.Element
  isDrag: (d: boolean) => void
  highlighted?: boolean
};

const defaultProps = {
  highlighted: false,
};

// The card props carried along with a drag. isPersonPage is deliberately not
// forwarded into the drag item, matching the previous behaviour.
type DraggablePersonProps = PersonProps & { isPersonPage?: boolean };

const DragWrapper = function DragWrapper({ children, isDrag, highlighted }: Props) {
  const selectedPeople = useSelector(selectSelectedPeople);
  const dragDisabled = selectedPeople.length > 0;
  const dragDisabledRef = useRef(dragDisabled);
  dragDisabledRef.current = dragDisabled;

  // children.props is a fresh object on every parent render. Keeping it in a ref
  // and resolving `item` lazily lets the drag source be registered once instead
  // of being torn down and rebuilt for every card on every render.
  const childPropsRef = useRef<DraggablePersonProps>(children.props as DraggablePersonProps);
  childPropsRef.current = children.props as DraggablePersonProps;

  const [{
    isDragging, offset,
  }, drag, preview] = useDrag(
    () => ({
      type: 'PEOPLE',
      item: () => {
        const { isPersonPage, ...restProps } = childPropsRef.current;
        return restProps as PersonProps;
      },
      canDrag: () => !dragDisabledRef.current,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
        // getDifferenceFromInitialOffset() is global drag state, so collecting
        // it unconditionally gave every mounted wrapper a new value on every
        // mousemove - re-rendering the whole list for the duration of a drag.
        // Only the source actually being dragged needs it.
        offset: monitor.isDragging() ? monitor.getDifferenceFromInitialOffset() : null,
      }),
    }),
    [],
  );

  const prevIsDragging = usePrevious(isDragging);

  useEffect(() => {
    if (prevIsDragging && !isDragging) isDrag(isDragging);
  }, [isDragging, isDrag, prevIsDragging]);

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  useEffect(() => {
    if (offset && offset.x && offset.y) {
      if (Math.abs(offset.x) > 70 || Math.abs(offset.y) > 70) { isDrag(true); }
    }
  }, [isDrag, offset]);

  return (
    <div
      ref={drag}
      style={dragInteractionStyle}
      className={classNames(
        {
          highlightedComponent: highlighted,
          downloadProfileBtn: highlighted,
          grabber: !isDragging && !dragDisabled,
          backActive: isDragging,
        },
      )}
    >
      {children || null}
    </div>
  );
};

export default React.memo(DragWrapper);
DragWrapper.defaultProps = defaultProps;
